"use client"

import { create } from "zustand"

import { getAuthContextState } from "@/features/auth/services/auth-context-store"
import {
  createMinistryTypeService,
  deleteMinistryTypeService,
  listMinistryTypesService,
  updateMinistryTypeService,
} from "@/features/church/config/ministries/services/ministry-types.service"
import {
  MinistryStatus,
  type CreateMinistryTypeInput,
  type MinistryType,
  type MinistryTypesListQuery,
  type MinistryTypesListResponse,
  type UpdateMinistryTypeInput,
} from "@/features/church/config/ministries/types/ministry.types"

type MinistryTypesState = {
  ministryTypes: MinistryType[]
  total: number
  page: number
  limit: number
  query: MinistryTypesListQuery
  isLoading: boolean
  listError: unknown
  actionError: unknown
  listMinistryTypes: (query?: MinistryTypesListQuery) => Promise<MinistryTypesListResponse>
  createMinistryType: (input: CreateMinistryTypeInput) => Promise<MinistryType>
  updateMinistryType: (id: string, input: UpdateMinistryTypeInput) => Promise<MinistryType>
  deleteMinistryType: (id: string) => Promise<void>
  setMinistryStatus: (id: string, status: MinistryStatus) => Promise<MinistryType>
  getById: (id: string) => MinistryType | undefined
}

const DEFAULT_LIST_LIMIT = 20

function withWorkspace(query: MinistryTypesListQuery): MinistryTypesListQuery {
  const workspaceId = query.workspaceId ?? getAuthContextState().activeWorkspaceId ?? undefined
  return {
    ...query,
    workspaceId,
  }
}

export const useMinistryTypesStore = create<MinistryTypesState>()((set, get) => ({
  ministryTypes: [],
  total: 0,
  page: 1,
  limit: DEFAULT_LIST_LIMIT,
  query: {
    q: "",
    page: 1,
    limit: DEFAULT_LIST_LIMIT,
    sortBy: "updatedAt",
    sortOrder: "desc",
  },
  isLoading: false,
  listError: null,
  actionError: null,

  getById: (id) => get().ministryTypes.find((item) => item.id === id),

  listMinistryTypes: async (query) => {
    const nextQuery = withWorkspace({
      ...get().query,
      ...query,
    })

    set({ isLoading: true, listError: null })

    try {
      const response = await listMinistryTypesService(nextQuery)

      set({
        ministryTypes: response.items,
        total: response.total,
        page: response.page,
        limit: response.limit,
        query: {
          ...nextQuery,
          page: response.page,
          limit: response.limit,
        },
        isLoading: false,
        listError: null,
      })

      return response
    } catch (error) {
      set({ isLoading: false, listError: error })
      throw error
    }
  },

  createMinistryType: async (input) => {
    set({ isLoading: true, actionError: null })

    try {
      const workspaceId =
        getAuthContextState().activeWorkspaceId ?? get().query.workspaceId ?? undefined
      const payload: CreateMinistryTypeInput =
        input.workspaceId === undefined ? { ...input, workspaceId } : input

      const created = await createMinistryTypeService(payload)
      await get().listMinistryTypes({ ...get().query })
      return created
    } catch (error) {
      set({ isLoading: false, actionError: error })
      throw error
    }
  },

  updateMinistryType: async (id, input) => {
    set({ isLoading: true, actionError: null })

    try {
      const updated = await updateMinistryTypeService(id, input)
      await get().listMinistryTypes({ ...get().query })
      return updated
    } catch (error) {
      set({ isLoading: false, actionError: error })
      throw error
    }
  },

  deleteMinistryType: async (id) => {
    set({ isLoading: true, actionError: null })

    try {
      await deleteMinistryTypeService(id)
      await get().listMinistryTypes({ ...get().query })
    } catch (error) {
      set({ isLoading: false, actionError: error })
      throw error
    }
  },

  setMinistryStatus: async (id, status) => {
    set({ isLoading: true, actionError: null })

    try {
      const updated = await updateMinistryTypeService(id, { status })
      set((state) => ({
        ministryTypes: state.ministryTypes.map((item) =>
          item.id === id
            ? {
                ...item,
                status: updated.status,
                updatedAt: updated.updatedAt,
              }
            : item
        ),
        isLoading: false,
        actionError: null,
      }))
      return updated
    } catch (error) {
      set({ isLoading: false, actionError: error })
      throw error
    }
  },
}))
